import { cn } from "@/cn";
import { PAGE_SIZE_OPTIONS } from "@/hooks/usePagination";

type OffsetPaginationBarProps = {
  mode?: "offset";
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  showingFrom: number;
  showingTo: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  className?: string;
};

type CursorPaginationBarProps = {
  mode: "cursor";
  page: number;
  pageSize: number;
  /** Items on the current page. */
  itemCount: number;
  showingFrom: number;
  showingTo: number;
  hasMore: boolean;
  canPrev: boolean;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onPageSizeChange: (size: number) => void;
  className?: string;
};

export type PaginationBarProps = OffsetPaginationBarProps | CursorPaginationBarProps;

export function PaginationBar(props: PaginationBarProps) {
  if (props.mode === "cursor") {
    const {
      page,
      pageSize,
      itemCount,
      showingFrom,
      showingTo,
      hasMore,
      canPrev,
      onFirst,
      onPrev,
      onNext,
      onPageSizeChange,
      className,
    } = props;

    if (itemCount === 0 && page <= 1) return null;

    return (
      <div
        className={cn(
          "mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between",
          className,
        )}
      >
        <p className="text-xs text-muted-foreground">
          {itemCount === 0
            ? "No items on this page"
            : hasMore
              ? `Showing ${showingFrom}–${showingTo}`
              : `Showing ${showingFrom}–${showingTo} (end)`}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Per page
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onFirst}
              disabled={!canPrev}
              className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="First page"
            >
              «
            </button>
            <button
              type="button"
              onClick={onPrev}
              disabled={!canPrev}
              className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <span className="px-2 text-xs text-muted-foreground">Page {page}</span>
            <button
              type="button"
              onClick={onNext}
              disabled={!hasMore}
              className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    page,
    pageSize,
    totalItems,
    totalPages,
    showingFrom,
    showingTo,
    onPageChange,
    onPageSizeChange,
    className,
  } = props;

  if (totalItems === 0) return null;

  return (
    <div
      className={cn(
        "mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <p className="text-xs text-muted-foreground">
        Showing {showingFrom}–{showingTo} of {totalItems}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Per page
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="First page"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Prev
          </button>
          <span className="px-2 text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            className="rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Last page"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}
